import * as React from "react";
import SelectBase, { SelectOption } from "./SelectBase";

interface SelectDataProps<T> {
  // Data source - either provide data + transform functions OR static options
  data?: T[];
  options?: SelectOption[];
  // Transform function (only needed if using data prop)
  getOptionLabel?: (item: T) => string;
  getOptionValue?: (item: T) => number | string;
  // Value handling
  value?: number | string | number[];
  values?: number[];
  // Multi select
  isMulti?: boolean;
  // Callbacks - onChange required for single select, onMultiChange required for multi select
  onChange?: (value?: number | string) => void;
  onMultiChange?: (values?: number[]) => void;
  // UI props
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  sx?: any;
  // Loading state
  isLoading?: boolean;
  isFetching?: boolean;
}

function SelectData<T>({
  data,
  options: staticOptions,
  getOptionLabel,
  getOptionValue,
  value,
  values,
  isMulti = false,
  onChange,
  onMultiChange,
  label,
  placeholder,
  error,
  helperText,
  sx,
  isLoading = false,
  isFetching = false,
}: SelectDataProps<T>) {
  // Transform data to options if provided
  const options = React.useMemo<SelectOption[]>(() => {
    if (staticOptions) {
      return staticOptions;
    }
    if (data && getOptionLabel && getOptionValue) {
      return data.map((item) => ({
        label: getOptionLabel(item),
        value: getOptionValue(item),
      }));
    }
    return [];
  }, [data, staticOptions, getOptionLabel, getOptionValue]);

  // Handle single select value
  const selectedSingle = React.useMemo(() => {
    if (isMulti) return null;
    return options.find((it) => it.value == value) || null;
  }, [options, value, isMulti]);

  // Handle multi select values
  const selectedMulti = React.useMemo<SelectOption[]>(() => {
    if (!isMulti || !values) return [];
    return options.filter((it) => values.includes(it.value as number));
  }, [options, values, isMulti]);

  // Handle single select change
  const handleSingleChange = React.useCallback(
    (newValue: SelectOption | SelectOption[] | null) => {
      if (!onChange) return;
      const singleValue = Array.isArray(newValue) ? newValue[0] : newValue;
      onChange(singleValue?.value as number | string | undefined);
    },
    [onChange]
  );

  // Handle multi select change
  const handleMultiChange = React.useCallback(
    (newValue: SelectOption | SelectOption[] | null) => {
      if (!onMultiChange) return;
      const multiValue = Array.isArray(newValue)
        ? newValue
        : newValue
          ? [newValue]
          : [];
      onMultiChange(multiValue.map((it) => it.value as number));
    },
    [onMultiChange]
  );

  return (
    <SelectBase
      options={options}
      value={isMulti ? selectedMulti : selectedSingle}
      onChange={isMulti ? handleMultiChange : handleSingleChange}
      isMulti={isMulti}
      label={label}
      placeholder={placeholder}
      error={error}
      helperText={helperText}
      isLoading={isLoading || isFetching}
      sx={sx}
    />
  );
}

export default SelectData;

